// PRD §29 — Executive summary. Renders the top recommendations from
// /api/report so the user gets a "what should I do next" answer before
// they have to read the underlying tables.
//
// When the engine finds nothing actionable, we show a quiet "all clear"
// note rather than hide the section — absence is information.
//
// M12.10 — each rec can be dismissed (✕) from the dashboard. Dismissal
// is persisted per-property in localStorage via
// useDismissedRecommendations. The PDF + digest builders don't consult
// this state, so the full list still renders in exports + emails.

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useDismissedRecommendations } from "@/hooks/useDismissedRecommendations";
import { cn } from "@/lib/utils";
import type { ReportRecommendation } from "@/lib/api";

interface ExecutiveSummaryProps {
  recommendations: ReportRecommendation[];
  /** Dashboard passes the current propertyId to enable per-property
   *  dismiss + restore via localStorage. The shared viewer (public
   *  /share/:token) omits it — viewers can't mutate the owner's
   *  experience and we don't want random dismiss UI on a read-only
   *  snapshot. */
  propertyId?: string | null;
}

export function ExecutiveSummary({ recommendations, propertyId }: ExecutiveSummaryProps) {
  const { dismissed, hiddenCount, dismiss, restoreAll, enabled } =
    useDismissedRecommendations(propertyId);

  if (recommendations.length === 0) {
    return (
      <div id="actions" className="scroll-mt-24 rounded-2xl bg-white p-6 ring-1 ring-neutral-950/5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700 ring-1 ring-green-100">
            <i className="fa-regular fa-check text-sm" aria-hidden="true" />
          </span>
          <div>
            <p className="font-display text-base font-semibold text-neutral-950">
              Nothing flagged this period
            </p>
            <p className="mt-1 text-sm text-neutral-600 text-pretty">
              Tagging looks clean, momentum is steady, and platform mix is reasonable. Keep the
              experiments running.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const visible = enabled
    ? recommendations.filter((r) => !dismissed.has(r.category))
    : recommendations;
  const allDismissed = enabled && visible.length === 0 && hiddenCount > 0;

  return (
    <div id="actions" className="scroll-mt-24 space-y-3">
      {enabled && hiddenCount > 0 ? (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-neutral-50 px-4 py-2 ring-1 ring-neutral-950/5">
          <p className="text-xs text-neutral-600">
            <span className="tabular-nums font-semibold text-neutral-800">{hiddenCount}</span>{" "}
            {hiddenCount === 1 ? "action" : "actions"} hidden on this property
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={restoreAll}
            className="px-2 py-1 text-neutral-700 hover:text-neutral-950"
          >
            <i className="fa-regular fa-rotate-left text-xs" aria-hidden="true" />
            Show all
          </Button>
        </div>
      ) : null}
      {allDismissed ? null : (
        <ul role="list" className="grid gap-3">
          {visible.map((rec, i) => (
            <li
              key={`${rec.category}-${i}`}
              className="flex items-start gap-3 rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5"
            >
              <SeverityGlyph severity={rec.severity} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                    {labelForCategory(rec.category)}
                  </span>
                  <SeverityChip severity={rec.severity} />
                </div>
                <p className="mt-1 text-sm text-neutral-800 text-pretty">{rec.message}</p>
                {rec.cta ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    asChild
                    className="mt-2 px-2 py-1 text-purple-700 hover:text-purple-900"
                  >
                    <Link to={rec.cta.href}>
                      {rec.cta.label}
                      <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                    </Link>
                  </Button>
                ) : null}
              </div>
              {enabled ? (
                <button
                  type="button"
                  onClick={() => dismiss(rec.category)}
                  aria-label={`Dismiss action: ${labelForCategory(rec.category)}`}
                  className="relative mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-50 hover:text-neutral-700 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
                >
                  <i className="fa-solid fa-xmark text-xs" aria-hidden="true" />
                  <span
                    aria-hidden="true"
                    className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2"
                  />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SeverityGlyph({ severity }: { severity: ReportRecommendation["severity"] }) {
  const tone =
    severity === "severe"
      ? "bg-red-50 text-red-700 ring-red-100"
      : severity === "medium"
        ? "bg-amber-50 text-amber-700 ring-amber-100"
        : "bg-purple-50 text-purple-700 ring-purple-100";
  const icon =
    severity === "severe"
      ? "fa-regular fa-circle-exclamation"
      : severity === "medium"
        ? "fa-regular fa-triangle-exclamation"
        : "fa-regular fa-lightbulb";
  return (
    <span
      className={cn(
        "mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full ring-1",
        tone,
      )}
    >
      <i className={`${icon} text-sm`} aria-hidden="true" />
    </span>
  );
}

function SeverityChip({ severity }: { severity: ReportRecommendation["severity"] }) {
  const tone =
    severity === "severe"
      ? "bg-red-50 text-red-700"
      : severity === "medium"
        ? "bg-amber-50 text-amber-700"
        : "bg-purple-50 text-purple-700";
  const label =
    severity === "severe" ? "Fix first" : severity === "medium" ? "Worth a look" : "Nudge";
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase", tone)}>
      {label}
    </span>
  );
}

function labelForCategory(category: string): string {
  switch (category) {
    case "momentum":
      return "Momentum";
    case "revenue":
      return "Revenue";
    case "engagement":
      return "Engagement";
    case "conversion":
      return "Conversions";
    case "platform-mix":
      return "Platform mix";
    case "utm-health":
      return "UTM health";
    default:
      return category;
  }
}
